import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import {
  getApiV1CourseregistrationEnrollmentCourseregistrationidCourseAccessTabQueryKey,
  usePutApiV1CourseregistrationEnrollmentCourseregistrationidCourseAccess,
} from '@/shared/api/generated';

import type { CourseAccess } from '../types/enrollment';

export interface CourseAccessForm {
  readonly isReleased: boolean;
  readonly releasedUntil: string;
  readonly releasedDays: string;
}

/**
 * Hook para edição do acesso ao curso de uma matrícula.
 * Gerencia o estado do formulário e a mutation PUT.
 *
 * @param enrollmentId - ID da matrícula (courseRegistrationId)
 * @param onSuccess - Callback chamado após salvar com sucesso
 */
export function useEditCourseAccess(enrollmentId: number, onSuccess: () => void) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<CourseAccessForm>({
    isReleased: false,
    releasedUntil: '',
    releasedDays: '',
  });

  const initForm = useCallback((courseAccess: CourseAccess) => {
    setForm({
      isReleased: courseAccess.isReleased ?? false,
      releasedUntil: courseAccess.releasedUntil ?? '',
      releasedDays: courseAccess.releasedDays != null ? String(courseAccess.releasedDays) : '',
    });
  }, []);

  const updateForm = useCallback(
    <K extends keyof CourseAccessForm>(key: K, value: CourseAccessForm[K]) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    [],
  );

  const { mutate, isPending: isSaving } =
    usePutApiV1CourseregistrationEnrollmentCourseregistrationidCourseAccess({
      mutation: {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey:
              getApiV1CourseregistrationEnrollmentCourseregistrationidCourseAccessTabQueryKey(
                enrollmentId,
              ),
          });
          onSuccess();
        },
      },
    });

  const save = useCallback(() => {
    mutate({
      courseRegistrationId: enrollmentId,
      data: {
        released: form.isReleased,
        releasedUntil: form.releasedUntil !== '' ? form.releasedUntil : null,
        releasedDays: form.releasedDays !== '' ? Number(form.releasedDays) : null,
      },
    });
  }, [mutate, enrollmentId, form]);

  return { form, initForm, updateForm, save, isSaving } as const;
}
